/**
 * /me/asset-requests — an employee's own work-tool requests (ADR-0024 #275 FE).
 *
 * A "Request a work tool" form (category + justification → a requisition for
 * THEMSELVES; the backend forces the employee) and a "My requests" tracker over
 * the caller's own requisitions. The tracker reuses GET /v1/me/assets (which
 * returns every status) — no new endpoint. An issued request becomes a holding,
 * shown on "My assets".
 */
import { createRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { meLayoutRoute } from './meLayout.js';
import { listMyAssets, listCategories, requestAsset, cancelRequest, type Holding } from '../api/assets.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';
import { AssignmentStatusBadge, fmtDate } from './assetShared.js';

export const meAssetRequestsRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: '/me/asset-requests',
  component: MeAssetRequestsPage,
});

// The tracker shows requisitions (not yet a holding). Issued → "My assets".
const REQUEST_STATUSES = new Set(['requested', 'approved', 'rejected', 'cancelled']);
const CANCELLABLE = new Set(['requested', 'approved']);

function MeAssetRequestsPage(): JSX.Element {
  const qc = useQueryClient();
  const mine = useQuery({ queryKey: ['me', 'assets'], queryFn: listMyAssets });
  const cats = useQuery({ queryKey: ['asset', 'categories'], queryFn: listCategories });
  const catLabel = useMemo(() => new Map((cats.data?.categories ?? []).map((c) => [c.id, c.label])), [cats.data]);
  const requests = (mine.data?.assets ?? []).filter((a) => REQUEST_STATUSES.has(a.status));
  const inProgress = requests.filter((a) => a.status === 'requested' || a.status === 'approved');
  const closed = requests.filter((a) => a.status === 'rejected' || a.status === 'cancelled');
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['me', 'assets'] });

  return (
    <section className="p-8 space-y-6 max-w-2xl">
      <header className="space-y-1">
        <h1 className="font-serif text-3xl">Work-tool requests</h1>
        <p className="text-sm text-gray-400">Request equipment you need. Your request goes to your manager to approve, then a custodian issues the item — it then shows on <span className="text-gray-300">My assets</span>.</p>
      </header>

      <RequestForm categories={(cats.data?.categories ?? []).filter((c) => c.active)} onRaised={invalidate} />

      <div className="space-y-3">
        <h2 className="text-sm uppercase tracking-wide text-gray-500">In progress</h2>
        {mine.isLoading ? <p className="text-sm text-gray-400">Loading…</p> : null}
        {!mine.isLoading && inProgress.length === 0 ? <p className="text-sm text-gray-400">No requests in progress.</p> : null}
        <ul className="space-y-2">
          {inProgress.map((a) => <RequestRow key={a.id} a={a} label={catLabel.get(a.categoryId) ?? a.categoryId} onChanged={invalidate} />)}
        </ul>
      </div>

      {closed.length > 0 ? (
        <div className="space-y-3">
          <h2 className="text-sm uppercase tracking-wide text-gray-500">Closed</h2>
          <ul className="space-y-2 opacity-70">
            {closed.map((a) => <RequestRow key={a.id} a={a} label={catLabel.get(a.categoryId) ?? a.categoryId} onChanged={invalidate} />)}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

function RequestForm({ categories, onRaised }: { categories: { id: string; label: string }[]; onRaised: () => void }): JSX.Element {
  const [categoryId, setCategoryId] = useState('');
  const [notes, setNotes] = useState('');
  const raise = useMutation({
    mutationFn: () => requestAsset({ categoryId, notes: notes.trim() || undefined }),
    onSuccess: () => { setCategoryId(''); setNotes(''); onRaised(); },
  });
  return (
    <form className="rounded border border-gray-900 p-4 space-y-3" onSubmit={(e) => { e.preventDefault(); if (categoryId) raise.mutate(); }}>
      <div className="text-sm font-medium text-gray-200">Request a work tool</div>
      <label className="block text-sm text-gray-300 space-y-1"><span>What do you need?</span>
        <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} aria-label="Category" className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm">
          <option value="">Select a category…</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </label>
      <label className="block text-sm text-gray-300 space-y-1"><span>Why? <span className="text-gray-500">(optional)</span></span>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} aria-label="Justification" rows={2} maxLength={500} placeholder="e.g. my laptop keeps crashing" className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
      </label>
      <button type="submit" disabled={!categoryId || raise.isPending} className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200 disabled:opacity-50">
        {raise.isPending ? 'Sending…' : 'Send request'}
      </button>
      <ApiErrorNotice error={raise.error} fallback="Could not raise the request." />
    </form>
  );
}

function RequestRow({ a, label, onChanged }: { a: Holding; label: string; onChanged: () => void }): JSX.Element {
  const cancel = useMutation({ mutationFn: () => cancelRequest(a.id), onSuccess: onChanged });
  return (
    <li className="rounded border border-gray-900 px-4 py-3 flex items-center justify-between text-sm">
      <div className="space-y-0.5">
        <div className="text-gray-200">{label}</div>
        {a.assetTag ? <div className="text-xs text-gray-500">{a.assetTag}{a.name ? ` · ${a.name}` : ''}</div> : null}
        <ApiErrorNotice error={cancel.error} fallback="Could not cancel." compact className="mt-1" />
      </div>
      <div className="flex items-center gap-3">
        <AssignmentStatusBadge status={a.status} />
        {CANCELLABLE.has(a.status) ? (
          <button type="button" onClick={() => cancel.mutate()} disabled={cancel.isPending} className="text-xs text-gray-400 underline hover:text-gray-200">Cancel</button>
        ) : (
          <span className="text-xs text-gray-600">{fmtDate(a.returnedAt)}</span>
        )}
      </div>
    </li>
  );
}
